import { z } from "zod";
import { billingStatusSchema } from "./billing";

// ─── Enums ────────────────────────────────────────────────────────────────────

export const shipmentStatusSchema = z.enum([
  "en_planificacion", "planificado", "activo", "en_curso", "finalizado", "cancelado",
]);

export const cancelReasonSchema = z.enum(["clima", "rotura", "chofer", "externo", "otro"]);
export type CancelReason = z.infer<typeof cancelReasonSchema>;

export const cancelShipmentSchema = z.object({
  reason:      cancelReasonSchema,
  description: z.string().min(1).max(500),
});
export type CancelShipment = z.infer<typeof cancelShipmentSchema>;

// ─── Shipment ─────────────────────────────────────────────────────────────────

export const shipmentIdSchema = z.string().uuid("ID de envío inválido");

const shipmentProductInputSchema = z.object({
  productId: z.string().uuid(),
  quantity:  z.string().nullable().optional(),
  weightKg:  z.string().nullable().optional(),
  notes:     z.string().nullable().optional(),
});

export const shipmentStopKindSchema = z.enum(["origin", "intermediate", "destination"]);

export const routeStopSchema = z.object({
  partyId: z.string().uuid(),
  kind:    shipmentStopKindSchema,
});

const shipmentInputSchema = z.object({
  clientId:    z.string().uuid().nullable().optional(),
  cargoTypeId: z.string().uuid().nullable().optional(),
  senderId:    z.string().uuid().nullable().optional(),
  recipientId: z.string().uuid().nullable().optional(),
  externalId:                    z.string().max(100).nullable().optional(),
  cargoValue:                    z.coerce.number().nonnegative().nullable().optional(),
  scheduledOriginArrivalAt:      z.string().datetime({ offset: true }).nullable().optional(),
  scheduledDestinationArrivalAt: z.string().datetime({ offset: true }).nullable().optional(),
  // Journey timestamps — in order
  originArrivalAt:               z.string().datetime({ offset: true }).nullable().optional(),
  originLoadingAt:               z.string().datetime({ offset: true }).nullable().optional(),
  originDepartureAt:             z.string().datetime({ offset: true }).nullable().optional(),
  customsOriginArrivalAt:        z.string().datetime({ offset: true }).nullable().optional(),
  customsOriginDepartureAt:      z.string().datetime({ offset: true }).nullable().optional(),
  borderOriginArrivalAt:         z.string().datetime({ offset: true }).nullable().optional(),
  borderOriginDepartureAt:       z.string().datetime({ offset: true }).nullable().optional(),
  borderCrossingAt:              z.string().datetime({ offset: true }).nullable().optional(),
  borderDestinationArrivalAt:    z.string().datetime({ offset: true }).nullable().optional(),
  borderDestinationDepartureAt:  z.string().datetime({ offset: true }).nullable().optional(),
  customsDestinationArrivalAt:   z.string().datetime({ offset: true }).nullable().optional(),
  customsDestinationDepartureAt: z.string().datetime({ offset: true }).nullable().optional(),
  destinationArrivalAt:          z.string().datetime({ offset: true }).nullable().optional(),
  destinationUnloadingAt:        z.string().datetime({ offset: true }).nullable().optional(),
  finalizedAt:                   z.string().datetime({ offset: true }).nullable().optional(),
  deliveryNoteNumber:         z.string().max(100).nullable().optional(),
  loadingWeightKg:            z.string().nullable().optional(),
  unloadingWeightKg:          z.string().nullable().optional(),
  deliveryNoteStatus:         z.enum(["normal", "validado", "reclamado"]).optional(),
  workLine:                   z.enum(["nacional", "internacional", "batea"]).nullable().optional(),
  loadingWindow:              z.string().max(50).nullable().optional(),
  finalDestinationId:         z.string().uuid().nullable().optional(),
  routeId:                   z.string().uuid().nullable().optional(),
  routeStops:                 z.array(routeStopSchema).optional(),
  mobileId:                   z.string().uuid().nullable().optional(),
  status:                     shipmentStatusSchema.optional(),
  billingStatus:              billingStatusSchema.nullable().optional(),
  isRouted:                   z.boolean().optional(),
  isMultiLeg:                 z.boolean().optional(),
  notes:                      z.string().nullable().optional(),
  billingNotes:               z.string().nullable().optional(),
  billingTags:                z.array(z.string().min(1).max(100)).nullable().optional(),
  internationalIds:           z.array(z.string().trim().min(1).max(50)).max(100).nullable().optional(),
  confirmInternationalIdsDeletion: z.boolean().optional(),
  tripPlan:                   z.record(z.string(), z.unknown()).nullable().optional(),
  techCheck:                  z.union([
    z.array(z.object({
      date:       z.string(),
      truck:      z.array(z.object({ item: z.string(), status: z.enum(["ok", "observado"]).nullable() })),
      trailer:    z.array(z.object({ item: z.string(), status: z.enum(["ok", "observado"]).nullable() })),
      savedAt:    z.string(),
      driverName: z.string().nullable().optional(),
    })),
    z.record(z.string(), z.unknown()), // legacy single-object format — still accepted
  ]).nullable().optional(),
  products:                   z.array(shipmentProductInputSchema).optional(),
});

const requireRouteForIntermediateStop = (data: z.infer<typeof shipmentInputSchema>, ctx: z.RefinementCtx) => {
  const stops = data.routeStops
  if (!stops) return

  if (stops.length < 2) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["routeStops"], message: "At least an origin and a destination stop are required" })
    return
  }

  if (stops[0]?.kind !== "origin") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["routeStops", 0, "kind"], message: "The first stop must be the origin" })
  }
  if (stops[stops.length - 1]?.kind !== "destination") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["routeStops", stops.length - 1, "kind"], message: "The last stop must be the destination" })
  }
  stops.slice(1, -1).forEach((stop, index) => {
    if (stop.kind !== "intermediate") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["routeStops", index + 1, "kind"], message: "Only intermediate stops are allowed between origin and destination" })
    }
  })

};

export const createShipmentSchema = shipmentInputSchema.superRefine(requireRouteForIntermediateStop);
export const updateShipmentSchema = shipmentInputSchema.partial().superRefine(requireRouteForIntermediateStop);

export const shipmentQuerySchema = z.object({
  deleted:           z.enum(["true", "false"]).optional(),
  status:            shipmentStatusSchema.optional(),
  workLine:          z.enum(["nacional", "internacional", "batea"]).optional(),
  excludeFinalizado: z.enum(["true", "false"]).optional(),
  page:              z.coerce.number().int().min(1).optional().default(1),
  pageSize:          z.coerce.number().int().min(1).max(500).optional().default(25),
  // Date range filter on scheduled_origin_arrival_at (YYYY-MM-DD)
  dateFrom:          z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo:            z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  // Server-side filters
  search:            z.string().optional(),
  statuses:          z.string().optional(),   // comma-separated shipment statuses
  workLines:         z.string().optional(),   // comma-separated work lines
  clientId:          z.string().uuid().optional(),
  noteStatus:        z.enum(["normal", "validado", "reclamado"]).optional(),
  noteStatuses:       z.string().optional(), // comma-separated delivery note statuses
  billingStatus:     z.enum(["pendiente", "controlado", "liquidado", "facturado", "cerrado"]).optional(),
  billingStatuses:    z.string().optional(), // comma-separated billing statuses
  isRouted:          z.enum(["true", "false"]).optional(),
  driverName:        z.string().optional(),
  operatorNames:     z.string().optional(),   // comma-separated operator names
  sortField:         z.enum(["internalId", "scheduledOriginArrivalAt", "unloadingWeightKg"]).optional().default("scheduledOriginArrivalAt"),
  sortDir:           z.enum(["asc", "desc"]).optional().default("desc"),
});

export const createBillingTagSchema = z.object({
  label: z.string().min(1).max(100),
})
export type CreateBillingTag = z.infer<typeof createBillingTagSchema>

// ─── Duplicate ───────────────────────────────────────────────────────────────

export const duplicateShipmentSchema = z.object({
  count: z.number().int().min(1).max(50),
})
export type DuplicateShipment = z.infer<typeof duplicateShipmentSchema>

// ─── Shipment docs ────────────────────────────────────────────────────────────

export const shipmentDocTypeSchema = z.enum(["remito_carga", "remito_descarga"])
export type ShipmentDocType = z.infer<typeof shipmentDocTypeSchema>

export const presignShipmentDocSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(100),
})

export const shipmentDocLetterSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]$/, "La letra debe ser un único carácter de la A a la Z")

export const createShipmentDocSchema = z.object({
  fileKey:  z.string().min(1).max(500),
  mimeType: z.string().min(1).max(100),
  fileName: z.string().max(255).nullable().optional(),
  notes:      z.string().nullable().optional(),
  docType:    shipmentDocTypeSchema.nullable().optional(),
  docTypeId:  z.string().uuid().nullable().optional(),
  // Omitted (driver app) → defaults to the type's control; explicit null → no letter.
  letter:     shipmentDocLetterSchema.nullable().optional(),
})

export const updateShipmentDocSchema = z.object({
  notes:     z.string().nullable().optional(),
  docType:   shipmentDocTypeSchema.nullable().optional(),
  docTypeId: z.string().uuid().nullable().optional(),
  letter:    shipmentDocLetterSchema.nullable().optional(),
})

export type PresignShipmentDoc = z.infer<typeof presignShipmentDocSchema>
export type UpdateShipmentDoc = z.infer<typeof updateShipmentDocSchema>
export type CreateShipmentDoc  = z.infer<typeof createShipmentDocSchema>

// ─── Types ────────────────────────────────────────────────────────────────────

export type ShipmentStatus = z.infer<typeof shipmentStatusSchema>;
export type ShipmentId     = z.infer<typeof shipmentIdSchema>;
export type CreateShipment = z.infer<typeof createShipmentSchema>;
export type UpdateShipment = z.infer<typeof updateShipmentSchema>;
export type ShipmentQuery  = z.infer<typeof shipmentQuerySchema>;
export type ShipmentStopKind = z.infer<typeof shipmentStopKindSchema>;
export type RouteStop = z.infer<typeof routeStopSchema>;

export const techCheckItemSchema = z.object({
  item:   z.string(),
  status: z.enum(["ok", "observado"]).nullable(),
});

export const techCheckEntrySchema = z.object({
  date:       z.string(),           // YYYY-MM-DD in driver's local timezone
  truck:      z.array(techCheckItemSchema),
  trailer:    z.array(techCheckItemSchema),
  savedAt:    z.string(),           // ISO timestamp of last save for this day
  driverName: z.string().nullable().optional(),
});

export const techCheckSchema = z.array(techCheckEntrySchema);

export type TechCheckItem  = z.infer<typeof techCheckItemSchema>;
export type TechCheckEntry = z.infer<typeof techCheckEntrySchema>;
export type TechCheck      = z.infer<typeof techCheckSchema>;

// ─── Billing import ───────────────────────────────────────────────────────────

const billingImportInternalIdSchema = z.preprocess((value) => {
  if (typeof value !== "string") return value
  const raw = value.trim()
  const normalized = /^SH\d+$/i.test(raw) ? raw.slice(2) : raw
  return /^\d+$/.test(normalized) ? Number(normalized) : value
}, z.number().int().positive())

export const billingImportRowSchema = z.object({
  internalId:   billingImportInternalIdSchema,
  billingNotes: z.string().max(1000).nullable().optional(),
  billingTags:  z.array(z.string().min(1).max(100)).nullable().optional(),
})

export const billingImportSchema = z.object({
  rows:    z.array(billingImportRowSchema).min(1).max(1000),
  preview: z.boolean().optional(), // true = dry-run (only resolve IDs, no DB write)
  // Bulk overrides — applied to ALL found shipments when present
  bulkBillingStatus: billingStatusSchema.nullable().optional(),
  bulkBillingNotes:  z.string().max(1000).nullable().optional(),
  bulkBillingTags:   z.array(z.string().min(1).max(100)).nullable().optional(),
})

export type BillingImportRow   = z.infer<typeof billingImportRowSchema>
export type BillingImportInput = z.infer<typeof billingImportSchema>
